import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileTextIcon } from '@radix-ui/react-icons';
import { apiUtils } from '../utils/apiUtils';
import api from '../services/api';
import {
	Card,
	CardContent,
	Button,
	Table,
	TableHeader,
	TableBody,
	TableHead,
	TableRow,
	TableCell,
	PageHeader,
	EmptyState,
	TableSkeleton,
	PlatformMark,
	useToast,
} from '../components/ui';

/**
 * Reports - the page a manager shows their client.
 *
 * The previous version was the only page in the app built with raw <table>,
 * raw <select> and raw <input> - no shared primitives at all - with plain-text
 * "Loading reports..." / "No reports generated yet." states. For the one
 * artifact that travels OUTSIDE the product (a studio forwarding a report up
 * the chain), looking like an intern project was the worst possible place.
 *
 * The export path also advertised a `format` parameter and silently did
 * nothing for any value except csv; it is now explicitly CSV.
 */
export default function Reports() {
	const toast = useToast();
	const [generating, setGenerating] = useState(false);
	const [selectedScanId, setSelectedScanId] = useState('');
	const [reportTitle, setReportTitle] = useState('');

	const {
		data: eligibleScans = [],
		isLoading: scansLoading,
	} = useQuery({
		queryKey: ['reports', 'eligible-scans'],
		queryFn: async () => (await api.get('/api/reports/eligible-scans'))?.data || [],
		staleTime: 60 * 1000,
	});

	const {
		data: reports = [],
		isLoading: reportsLoading,
		refetch: refetchReports,
	} = useQuery({
		queryKey: ['reports', 'list'],
		queryFn: async () => (await api.get('/api/reports'))?.data || [],
		staleTime: 60 * 1000,
	});

	const generateReport = async () => {
		try {
			setGenerating(true);
			await api.post('/api/reports/generate', {
				scanId: selectedScanId,
				title: reportTitle || undefined,
			});
			setReportTitle('');
			toast.success('Report generated');
			await refetchReports();
		} catch (err) {
			toast.error('Could not generate the report', { description: err.message });
		} finally {
			setGenerating(false);
		}
	};

	const exportCsv = async (reportId) => {
		try {
			// Blob download - the one call that stays on the raw client.
			const response = await apiUtils.get(`/api/reports/${reportId}/export`, {
				params: { format: 'csv' },
				responseType: 'blob',
			});
			const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
			const url = window.URL.createObjectURL(blob);
			const anchor = document.createElement('a');
			anchor.href = url;
			anchor.download = `repcheck-report-${reportId}.csv`;
			document.body.appendChild(anchor);
			anchor.click();
			anchor.remove();
			window.URL.revokeObjectURL(url);
		} catch (err) {
			toast.error('Could not export the report', { description: err.message });
		}
	};

	const riskTone = (level) => {
		const l = String(level || '').toLowerCase();
		if (l === 'critical') return 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300';
		if (l === 'high') return 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300';
		if (l === 'medium' || l === 'moderate') return 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300';
		return 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300';
	};

	return (
		<div className="min-h-screen bg-muted">
			<div className="max-w-7xl mx-auto p-4 sm:p-6">
				<PageHeader
					title="Reports"
					description="Stakeholder-ready summaries from completed scans, exportable for sharing."
				/>

				<div className="grid gap-6 lg:grid-cols-3 items-start">
					<Card className="lg:col-span-1">
						<CardContent className="p-6">
							<h2 className="text-base font-semibold text-foreground">Generate a report</h2>
							<p className="mt-1 text-sm text-muted-foreground">From any completed scan.</p>
							<div className="mt-4 space-y-4">
								<select
									value={selectedScanId}
									onChange={(event) => setSelectedScanId(event.target.value)}
									className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
									disabled={scansLoading || eligibleScans.length === 0}
									aria-label="Completed scan"
								>
									<option value="">
										{scansLoading ? 'Loading scans…' : eligibleScans.length === 0 ? 'No completed scans yet' : 'Select a completed scan'}
									</option>
									{eligibleScans.map((scan) => (
										<option key={scan._id} value={scan._id}>
											{scan.client?.name || 'Unknown client'} · @{scan.account?.handle || 'unknown'}
										</option>
									))}
								</select>
								<input
									type="text"
									value={reportTitle}
									onChange={(event) => setReportTitle(event.target.value)}
									className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
									placeholder="Report title (optional)"
								/>
								<Button
									className="w-full"
									onClick={generateReport}
									disabled={generating || !selectedScanId}
									loading={generating}
								>
									Generate report
								</Button>
							</div>
						</CardContent>
					</Card>

					<div className="lg:col-span-2">
						{reportsLoading ? (
							<TableSkeleton rows={4} />
						) : reports.length === 0 ? (
							<Card>
								<CardContent>
									<EmptyState
										icon={FileTextIcon}
										title="No reports yet"
										description="Generate a report from a completed scan - it lands here, ready to export and share."
									/>
								</CardContent>
							</Card>
						) : (
							<div className="rounded-lg border border-border bg-card overflow-x-auto min-h-[16rem]">
								<Table>
									<TableHeader>
										<TableRow className="bg-muted">
											<TableHead>Report</TableHead>
											<TableHead>Client</TableHead>
											<TableHead>Risk</TableHead>
											<TableHead>Created</TableHead>
											<TableHead className="text-right">Actions</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{reports.map((report) => (
											<TableRow key={report._id}>
												<TableCell>
													<div className="flex items-center gap-2 min-w-0">
														{report.platform && <PlatformMark platform={report.platform} size="xs" soft />}
														<span className="font-medium text-foreground truncate">{report.title}</span>
													</div>
												</TableCell>
												<TableCell className="text-sm text-foreground">{report.client?.name || 'Unknown'}</TableCell>
												<TableCell>
													<span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${riskTone(report.riskLevel)}`}>
														{report.riskLevel || 'low'}
													</span>
												</TableCell>
												<TableCell className="text-sm text-muted-foreground">
													{new Date(report.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
												</TableCell>
												<TableCell className="text-right">
													<Button variant="outline" size="sm" onClick={() => exportCsv(report._id)}>
														Export CSV
													</Button>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
